/**
 * One revision's assessment mix, weighted by module credits.
 *
 * @module components/portfolio/comparison/AssessmentProfile
 */

import React from "react";

import { DonutChart, Panel } from "@/components/ui";
import type { AssessmentTypeSlice } from "@/utils/assessments";

export interface AssessmentProfileProps {
  /** The revision this side belongs to. */
  label: string;
  slices: readonly AssessmentTypeSlice[];
}

/**
 * @example
 * ```tsx
 * <AssessmentProfile label={revision.institutionalLabel} slices={earlierProfile} />
 * ```
 */
export function AssessmentProfile({ label, slices }: AssessmentProfileProps): React.ReactElement {
  return (
    <Panel
      tone="inset"
      headingLevel={4}
      title={label}
      // Two profiles of the same shape sit beside each other, and both are
      // headed by a revision label alone, so the region needs the noun too.
      aria-label={`${label} assessment profile`}
    >
      <DonutChart
        slices={slices.map((slice) => ({ label: slice.type, percent: slice.pct }))}
        emptyMessage="No weighted assessments recorded."
      />
    </Panel>
  );
}
